// frontend/src/components/TrialWarningBanner.jsx
import { useEffect, useState } from "react";
import { getTenantSlug } from "../lib/tenant.js";

/**
 * Banner that shows trial period warnings when approaching expiration.
 *
 * Shows when:
 * - Tenant status is 'trial'
 * - Trial ends within 7 days
 *
 * You can add this to your StaffLayout or PortalApp components.
 */
export default function TrialWarningBanner() {
  const [trialInfo, setTrialInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    async function checkTrialStatus() {
      try {
        const tenantSlug = getTenantSlug();
        if (!tenantSlug) return;

        // You'll need to create this endpoint or add trial info to an existing one
        const response = await fetch("/api/me", {
          headers: {
            "X-Tenant-Slug": tenantSlug,
          },
        });

        if (response.ok) {
          const data = await response.json();

          // Assuming your /api/me endpoint returns tenant info with trial data
          if (data.tenant?.status === "trial" && data.tenant?.trial_ends_at) {
            const now = new Date();
            const endsAt = new Date(data.tenant.trial_ends_at);
            const daysRemaining = Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24));

            // Only show if trial ends within 7 days
            if (daysRemaining <= 7 && daysRemaining > 0) {
              setTrialInfo({
                daysRemaining,
                endsAt: data.tenant.trial_ends_at,
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to check trial status:", error);
      }
    }

    checkTrialStatus();

    // Check every hour
    const interval = setInterval(checkTrialStatus, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!trialInfo || dismissed) {
    return null;
  }

  const isUrgent = trialInfo.daysRemaining <= 3;
  const backgroundColor = isUrgent ? "#FEF2F2" : "#FEF3C7";
  const textColor = isUrgent ? "#991B1B" : "#92400E";
  const borderColor = isUrgent ? "#FCA5A5" : "#FCD34D";

  return (
    <div
      style={{
        backgroundColor,
        borderBottom: `2px solid ${borderColor}`,
        padding: "0.75rem 1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span style={{ fontSize: "1.25rem" }}>⚠️</span>
        <span style={{ color: textColor, fontWeight: "500" }}>
          Trial expires in {trialInfo.daysRemaining} {trialInfo.daysRemaining === 1 ? "day" : "days"}
        </span>
        <span style={{ color: textColor, opacity: 0.8, fontSize: "0.875rem" }}>({new Date(trialInfo.endsAt).toLocaleDateString()})</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <a
          href="mailto:support@cbcfx.site"
          style={{
            color: textColor,
            textDecoration: "underline",
            fontWeight: "500",
            fontSize: "0.875rem",
          }}>
          Contact Support
        </a>
        <button
          onClick={() => setDismissed(true)}
          style={{
            background: "transparent",
            border: "none",
            color: textColor,
            cursor: "pointer",
            fontSize: "1.25rem",
            padding: "0",
            lineHeight: 1,
          }}
          aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  );
}
